import React, { useEffect, useState, useContext, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import CollapseHeader from '../../core/common/collapse-header';
import { AuthContext } from '../../context/authcontext';
import { API_BASE_URL } from '../../environment';

const RedirectPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { auth, selectedTenantId } = useContext(AuthContext);
    const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
    const [message, setMessage] = useState("Processing your integration... Please wait.");
    const hasFetched = useRef(false);

    useEffect(() => {
        const processIntegration = async () => {
            const code = searchParams.get("code");
            const shop_id = searchParams.get("shop_id");
            const state = searchParams.get("state");
            const provider = localStorage.getItem("last_auth_provider");

            if (!code) {
                setStatus("error");
                setMessage("Authorization code missing from the URL. Please try again.");
                return;
            }

            if (!provider) {
                setStatus("error");
                setMessage("Unknown integration provider. Please try reconnecting from Settings.");
                return;
            }

            if (!auth || !selectedTenantId) {
                setStatus("error");
                setMessage("User authentication context is missing. Please log in again.");
                return;
            }

            if (hasFetched.current) return;
            hasFetched.current = true;

            const endpoint =
                provider === "TikTok" ? "tiktok" :
                provider === "Lazada" ? "lazada" :
                provider === "GoogleChat" ? "google-chat" :
                provider === "GoogleCalendar" ? "google-calendar" :
                "shopee";
            const tenantId = selectedTenantId;
            const userId = auth.user_id;

            try {
                const response = await fetch(`${API_BASE_URL}/${endpoint}/fetch-token/`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Basic ${localStorage.getItem("authCred")}`,
                    },
                    body: JSON.stringify({
                        code: code,
                        ...(endpoint === "shopee" && { shop_id: shop_id }),
                        ...(endpoint === "google-chat" && { state: state }),
                        tenant_id: tenantId,
                        user_id: userId
                    })
                });

                if (response.ok) {
                    setStatus("success");
                    setMessage(`${provider} integration completed successfully!`);
                } else {
                    const errorData = await response.json().catch(() => ({}));
                    setStatus("error");
                    setMessage(errorData.message || `Failed to complete ${provider} integration.`);
                }
            } catch (err) {
                setStatus("error");
                setMessage(`An error occurred while connecting to ${provider}. Please check your connection and try again.`);
            }
        };

        processIntegration();
    }, [searchParams, selectedTenantId, auth]);

    return (
        <div className="page-wrapper">
            <div className="content">
                <div className="row">
                    <div className="col-md-12">
                        <div className="page-header">
                            <div className="row align-items-center">
                                <div className="col-8">
                                    <h3 className="page-title">Integration Status</h3>
                                </div>
                                <div className="col-4 text-sm-end">
                                    <div className="title-head">
                                        <div className="head-icons">
                                            <CollapseHeader />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card shadow-sm border-0 mt-4 rounded-3">
                            <div className="card-body text-center py-5">
                                {status === "loading" && (
                                    <>
                                        <div className="spinner-border text-primary mb-4" role="status" style={{ width: "3rem", height: "3rem" }}>
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <h3 className="fw-bold mb-3">Connecting...</h3>
                                    </>
                                )}
                                {status === "success" && (
                                    <>
                                        <div className="mb-4">
                                            <i className="ti ti-circle-check text-success" style={{ fontSize: "5rem" }}></i>
                                        </div>
                                        <h3 className="fw-bold text-success mb-3">Success!</h3>
                                    </>
                                )}
                                {status === "error" && (
                                    <>
                                        <div className="mb-4">
                                            <i className="ti ti-alert-circle text-danger" style={{ fontSize: "5rem" }}></i>
                                        </div>
                                        <h3 className="fw-bold text-danger mb-3">Integration Failed</h3>
                                    </>
                                )}

                                <p className="text-muted mb-5" style={{ fontSize: '14px' }}>{message}</p>

                                {(status === "success" || status === "error") && (
                                    <button
                                        className="btn btn-primary px-3 py-2 rounded-pill shadow-sm"
                                        onClick={() => {
                                            const provider = localStorage.getItem("last_auth_provider");
                                            navigate(provider === "GoogleCalendar" ? "/integration-settings/calendar-settings" : "/integration-settings/chat-settings");
                                        }}
                                        style={{ fontSize: "1rem" }}
                                    >
                                        <i className="fa fa-arrow-left me-2"></i>
                                        Go Back
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default RedirectPage;

